import { useStorage } from "@vueuse/core";
import type { DataTableColumnResizeEndEvent } from "primevue/datatable";
import { pickBy, sum } from "lodash-es";
import { computed, toValue, type MaybeRefOrGetter } from "vue";

const FIXED_COLUMN = "*";
const LAYOUT_SEPARATOR = "|";

/**
 * Remembers the column widths a user drags a resizable PrimeVue DataTable to, in localStorage.
 *
 * Used instead of the DataTable's own `stateStorage`, which also persists sort, filters, pagination,
 * expansion and selection, stores widths in pixels by column position, and reads `window.localStorage`
 * unguarded (a browser that blocks site data throws on every render). Here only widths are kept, as
 * fractions of the table width, so a layout dragged on a wide monitor still fits a laptop. Storage
 * that is unavailable or holds garbage just means default widths.
 *
 * `columnKeys` names the rendered columns in order; `null` marks a column whose width the component
 * fixes itself (a checkbox column), which is neither stored nor restored.
 *
 * Widths are remembered per layout — the rendered columns, fixed ones included — and restored only
 * for the same layout, with the last column left without a width to take whatever remains. Widths
 * measured without a column that comes and goes leave it no room when it is back, and so do
 * fractions whose rounding adds up to more than the table: Firefox then squeezes the unsized column
 * to nearly zero width and its header wraps one letter per line.
 *
 * Bind `columnStyle(key)` to each tracked Column's `style` and `onColumnResizeEnd` to the table's
 * `@column-resize-end`.
 */
export function usePersistedColumnWidths(
  storageKey: string,
  columnKeys: MaybeRefOrGetter<(string | null)[]>
) {
  const layouts = useStorage<Record<string, Record<string, number>>>(
    storageKey,
    {},
    undefined,
    // Losing a remembered width is harmless, so a blocked or full storage stays quiet.
    { writeDefaults: false, onError: () => {} }
  );

  const layoutKey = computed(() =>
    toValue(columnKeys)
      .map((key) => key ?? FIXED_COLUMN)
      .join(LAYOUT_SEPARATOR)
  );

  const restoredWidths = computed<Map<string, number>>(() => {
    const layout = layouts.value?.[layoutKey.value];
    if (!isLayout(layout)) return new Map();
    const keys = toValue(columnKeys).filter((key) => key !== null);
    const stored = keys.map((key) => layout[key]);
    if (!allFractions(stored)) return new Map();

    const sized = keys.slice(0, -1);
    const sizedFractions = stored.slice(0, -1);
    if (sizedFractions.reduce((total, fraction) => total + fraction, 0) >= 1)
      return new Map();
    return new Map(sized.map((key, index) => [key, sizedFractions[index]]));
  });

  const columnStyle = (key: string): { width: string } | undefined => {
    const fraction = restoredWidths.value.get(key);
    if (fraction === undefined) return undefined;
    return { width: `${(fraction * 100).toFixed(2)}%` };
  };

  const onColumnResizeEnd = ({ element }: DataTableColumnResizeEndEvent) => {
    const cells = Array.from(element.parentElement?.children ?? []).map(
      (cell) => (cell as HTMLElement).offsetWidth
    );
    const keys = toValue(columnKeys);
    const tableWidth = sum(cells);
    if (cells.length !== keys.length || tableWidth <= 0) return;

    const measured = Object.fromEntries(
      keys.flatMap((key, index) =>
        key === null ? [] : [[key, round(cells[index] / tableWidth)]]
      )
    );
    layouts.value = {
      ...pickBy(layouts.value ?? {}, isLayout),
      [layoutKey.value]: measured,
    };
  };

  return { columnStyle, onColumnResizeEnd };
}

const isLayout = (value: unknown): value is Record<string, number> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const round = (fraction: number): number =>
  Math.round(fraction * 10000) / 10000;

const allFractions = (values: unknown[]): values is number[] =>
  values.every((value) => typeof value === "number" && value > 0 && value < 1);
